import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, XCircle } from 'lucide-react';
import type { BillingStatus, PlanTier } from '@/lib/billing';
import { isTrialExpired, isAccountBlocked } from '@/lib/billing';
import { useNavigate } from 'react-router-dom';

interface BillingStatusBannerProps {
  billingStatus: BillingStatus | null;
  trialEndsAt: string | null;
  planTier: PlanTier | null;
  suspendedAt?: string | null;
}

export default function BillingStatusBanner({ billingStatus, trialEndsAt, planTier, suspendedAt }: BillingStatusBannerProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    if (planTier === 'school') {
      navigate('/school-billing');
    } else {
      navigate('/individual-billing');
    }
  };

  if (suspendedAt) {
    return (
      <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
        <XCircle className="h-4 w-4" />
        <AlertDescription>
          This school account has been suspended. Contact{' '}
          <a href="mailto:support@writeassess.co.uk" className="font-medium underline">support@writeassess.co.uk</a>{' '}
          for assistance.
        </AlertDescription>
      </Alert>
    );
  }

  if (!billingStatus) return null;

  if (billingStatus === 'past_due') {
    return (
      <Alert variant="default" className="rounded-none border-x-0 border-t-0 bg-amber-50 border-amber-200 text-amber-900">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800 flex items-center justify-between gap-4">
          <span>
            Your payment is past due. Please update your billing details to avoid interruption — contact{' '}
            <a href="mailto:support@writeassess.co.uk" className="font-medium underline">support@writeassess.co.uk</a>
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  if (billingStatus === 'cancelled' || billingStatus === 'paused') {
    return (
      <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
        <XCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>Your subscription has ended. Reactivate your plan to continue using WriteAssess.</span>
          <Button size="sm" variant="outline" onClick={handleUpgrade} className="shrink-0">
            Reactivate Plan
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (isTrialExpired(trialEndsAt, billingStatus)) {
    return (
      <Alert variant="default" className="rounded-none border-x-0 border-t-0 bg-amber-50 border-amber-200 text-amber-900">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800 flex items-center justify-between gap-4">
          <span>Your free trial has ended. Upgrade to continue using WriteAssess.</span>
          <Button size="sm" onClick={handleUpgrade} className="shrink-0">Upgrade Now</Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!isAccountBlocked(billingStatus, trialEndsAt)) return null;

  return null;
}
